import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Dropdown,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import { MARKETPLACE_ICONS } from '../../assets/marketplaceIcons';
import { AddStoreModal } from '../../components/Store';
import api from '../../services/api';

const getStoreIcon = (storeType) => MARKETPLACE_ICONS[storeType] || MARKETPLACE_ICONS.wildberries;

function formatBalance(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(n)) return '—';
  return `¥${n.toFixed(2)}`;
}

function emptyStats() {
  return {
    total_returns: 0,
    pending_decision: 0,
    submitted_for_sale: 0,
    in_transit: 0,
    other_pvz_in_transit: 0,
    in_processing: 0,
    wh_sale: 0,
    completed: 0,
  };
}

export default function StoresTopic({ onStoresChange }) {
  const { t } = useLanguage();
  const [stores, setStores] = useState([]);
  const [balancesByPk, setBalancesByPk] = useState({});
  const [statsByExternalId, setStatsByExternalId] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddStore, setShowAddStore] = useState(false);
  const [showEditStore, setShowEditStore] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [editStoreName, setEditStoreName] = useState('');
  const [editStoreLoading, setEditStoreLoading] = useState(false);
  const [editStoreError, setEditStoreError] = useState('');
  const [showDeleteStore, setShowDeleteStore] = useState(false);
  const [deletingStore, setDeletingStore] = useState(null);
  const [deleteStoreLoading, setDeleteStoreLoading] = useState(false);
  const [showUpdateApiKey, setShowUpdateApiKey] = useState(false);
  const [updatingStore, setUpdatingStore] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [updateApiKeyLoading, setUpdateApiKeyLoading] = useState(false);
  const [updateApiKeyError, setUpdateApiKeyError] = useState('');
  const [updateApiKeySuccess, setUpdateApiKeySuccess] = useState('');

  const notifyStoresChange = useCallback(
    (nextStores) => {
      if (typeof onStoresChange === 'function') {
        onStoresChange(nextStores);
      }
    },
    [onStoresChange],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [storesRes, billingRes] = await Promise.all([
        api.getStores(),
        api.getBillingExpenses({ page: 1, page_size: 1 }),
      ]);
      const storeList = Array.isArray(storesRes) ? storesRes : [];
      setStores(storeList);

      const balanceRows = Array.isArray(billingRes?.stores_balance) ? billingRes.stores_balance : [];
      const balanceMap = {};
      balanceRows.forEach((row) => {
        if (row?.store_id != null) {
          balanceMap[row.store_id] = row.total_amount;
        }
      });
      setBalancesByPk(balanceMap);

      const statsEntries = await Promise.all(
        storeList.map(async (store) => {
          const externalId = (store.store_id || '').trim();
          if (!externalId) {
            return [externalId, emptyStats()];
          }
          try {
            const res = await api.getReturnsStatistics({ store_id: externalId });
            const data = res?.data ?? res;
            return [
              externalId,
              {
                total_returns: Number(data?.total_returns) || 0,
                pending_decision: Number(data?.pending_decision) || 0,
                submitted_for_sale: Number(data?.submitted_for_sale) || 0,
                in_transit: Number(data?.in_transit) || 0,
                other_pvz_in_transit: Number(data?.other_pvz_in_transit) || 0,
                in_processing: Number(data?.in_processing) || 0,
                wh_sale: Number(data?.wh_sale) || 0,
                completed: Number(data?.completed) || 0,
              },
            ];
          } catch {
            return [externalId, emptyStats()];
          }
        }),
      );
      setStatsByExternalId(Object.fromEntries(statsEntries));
      notifyStoresChange(storeList);
    } catch (err) {
      console.error('Failed to load stores overview:', err);
    } finally {
      setLoading(false);
    }
  }, [notifyStoresChange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const pendingStores = stores.filter((s) => s.verification_status === 'pending');
    if (pendingStores.length === 0) return undefined;
    const tick = async () => {
      for (const store of pendingStores) {
        try {
          const response = await api.getStoreVerificationStatus(store.id);
          if (response.verification_status !== 'pending') {
            loadData();
            break;
          }
        } catch (err) {
          console.error(err);
        }
      }
    };
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [stores, loadData]);

  const handleEditStore = (store) => {
    setEditingStore(store);
    setEditStoreName(store.name || '');
    setEditStoreError('');
    setShowEditStore(true);
  };

  const handleDeleteStore = (store) => {
    setDeletingStore(store);
    setShowDeleteStore(true);
  };

  const confirmDeleteStore = async () => {
    if (!deletingStore) return;
    setDeleteStoreLoading(true);
    try {
      await api.deleteStore(deletingStore.id);
      const next = stores.filter((s) => s.id !== deletingStore.id);
      setStores(next);
      notifyStoresChange(next);
      setShowDeleteStore(false);
      setDeletingStore(null);
    } finally {
      setDeleteStoreLoading(false);
    }
  };

  const handleSaveStoreName = async () => {
    if (!editingStore) return;
    setEditStoreLoading(true);
    setEditStoreError('');
    try {
      await api.updateStore(editingStore.id, {
        name: editStoreName.trim() || `Store ${editingStore.store_id}`,
      });
      const next = stores.map((s) =>
        s.id === editingStore.id
          ? { ...s, name: editStoreName.trim() || `Store ${editingStore.store_id}` }
          : s,
      );
      setStores(next);
      notifyStoresChange(next);
      setShowEditStore(false);
    } catch (err) {
      setEditStoreError(err.data?.detail || t('stores.editError'));
    } finally {
      setEditStoreLoading(false);
    }
  };

  const handleUpdateApiKey = (store) => {
    setUpdatingStore(store);
    setNewApiKey('');
    setUpdateApiKeyError('');
    setUpdateApiKeySuccess('');
    setShowUpdateApiKey(true);
  };

  const handleSaveApiKey = async () => {
    if (!updatingStore || !newApiKey.trim()) return;
    setUpdateApiKeyLoading(true);
    setUpdateApiKeyError('');
    setUpdateApiKeySuccess('');
    try {
      await api.updateStoreApiKey(updatingStore.id, newApiKey.trim());
      setUpdateApiKeySuccess(t('stores.apiKeyUpdateSuccess'));
      const next = stores.map((s) =>
        s.id === updatingStore.id ? { ...s, verification_status: 'pending' } : s,
      );
      setStores(next);
      notifyStoresChange(next);
      setTimeout(() => setShowUpdateApiKey(false), 1200);
    } catch (err) {
      setUpdateApiKeyError(err.data?.detail || t('stores.apiKeyUpdateError'));
    } finally {
      setUpdateApiKeyLoading(false);
    }
  };

  const handleStoreAdded = (store) => {
    const next = [...stores, store];
    setStores(next);
    notifyStoresChange(next);
    setShowAddStore(false);
    loadData();
  };

  const renderVerificationBadge = (store) => {
    if (store.verification_status === 'verified') {
      return <Badge bg="success">{t('stores.connected')}</Badge>;
    }
    if (store.verification_status === 'pending') {
      return <Badge bg="warning" text="dark">{t('stores.verifying')}</Badge>;
    }
    if (store.verification_status === 'failed') {
      return <Badge bg="danger">{t('stores.verificationFailed')}</Badge>;
    }
    return null;
  };

  const statCell = (value) => (
    <span className={value > 0 ? 'fw-semibold' : 'text-muted'}>{value}</span>
  );

  return (
    <div className="chat-app-stores-page">
      <div className="chat-app-stores-page-header">
        <p className="text-muted small mb-0">{t('chatApp.storesPageHint')}</p>
        <Button size="sm" className="btn-gradient" onClick={() => setShowAddStore(true)}>
          <i className="bi bi-plus-lg me-1" />
          {t('nav.addStore')}
        </Button>
      </div>

      {stores.some((s) => s.verification_status === 'failed') && (
        <Alert variant="danger" className="py-2 small mb-3">
          {t('stores.verificationFailedMessage')}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          {t('chatApp.loading')}
        </div>
      ) : stores.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <p className="text-muted mb-3">{t('stores.noStores')}</p>
            <Button className="btn-gradient" onClick={() => setShowAddStore(true)}>
              <i className="bi bi-plus-lg me-1" />
              {t('stores.addFirst')}
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="chat-app-stores-page-content d-none d-lg-flex">
            <div className="chat-app-stores-table-wrap">
              <div className="chat-app-stores-table-scroll">
                <Table hover size="sm" className="chat-app-stores-table mb-0">
              <thead>
                <tr>
                  <th>{t('stores.store')}</th>
                  <th>{t('chatApp.storeBalance')}</th>
                  <th>{t('chatApp.returnsTotal')}</th>
                  <th>{t('chatApp.navPendingDecision')}</th>
                  <th>{t('returns.statusSubmittedForSale')}</th>
                  <th>{t('returns.statusAwaitingReceipt')}</th>
                  <th>{t('chatApp.storesOtherPvz')}</th>
                  <th>{t('chatApp.storesInProcessing')}</th>
                  <th>{t('chatApp.storesWhSale')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {stores.map((store) => {
                  const stats = statsByExternalId[store.store_id] || emptyStats();
                  return (
                    <tr key={store.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <img
                            src={getStoreIcon(store.store_type)}
                            alt=""
                            className="chat-app-store-icon"
                          />
                          <div className="min-w-0">
                            <div className="fw-semibold text-truncate">
                              {store.name || `Store ${store.store_id}`}
                            </div>
                            <small className="text-muted d-block">{store.store_id}</small>
                            {renderVerificationBadge(store)}
                          </div>
                        </div>
                      </td>
                      <td>{formatBalance(balancesByPk[store.id])}</td>
                      <td>{statCell(stats.total_returns)}</td>
                      <td>{statCell(stats.pending_decision)}</td>
                      <td>{statCell(stats.submitted_for_sale)}</td>
                      <td>{statCell(stats.in_transit)}</td>
                      <td>{statCell(stats.other_pvz_in_transit)}</td>
                      <td>{statCell(stats.in_processing)}</td>
                      <td>{statCell(stats.wh_sale)}</td>
                      <td className="text-end">
                        <StoreActionsDropdown
                          store={store}
                          onEdit={handleEditStore}
                          onUpdateApiKey={handleUpdateApiKey}
                          onDelete={handleDeleteStore}
                          t={t}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
              </div>
            </div>
          </div>

          <div className="chat-app-stores-cards d-lg-none">
            {stores.map((store) => {
              const stats = statsByExternalId[store.store_id] || emptyStats();
              return (
                <Card key={store.id} className="border-0 shadow-sm mb-2 chat-app-store-card">
                  <Card.Body className="py-3">
                    <div className="d-flex align-items-start gap-2 mb-2">
                      <img
                        src={getStoreIcon(store.store_type)}
                        alt=""
                        className="chat-app-store-icon"
                      />
                      <div className="flex-grow-1 min-w-0">
                        <div className="fw-semibold">{store.name || `Store ${store.store_id}`}</div>
                        <small className="text-muted">{store.store_id}</small>
                        <div className="mt-1">{renderVerificationBadge(store)}</div>
                      </div>
                      <StoreActionsDropdown
                        store={store}
                        onEdit={handleEditStore}
                        onUpdateApiKey={handleUpdateApiKey}
                        onDelete={handleDeleteStore}
                        t={t}
                      />
                    </div>
                    <div className="chat-app-store-stats-grid">
                      <StoreStat label={t('chatApp.storeBalance')} value={formatBalance(balancesByPk[store.id])} />
                      <StoreStat label={t('chatApp.returnsTotal')} value={stats.total_returns} />
                      <StoreStat label={t('chatApp.navPendingDecision')} value={stats.pending_decision} />
                      <StoreStat label={t('returns.statusSubmittedForSale')} value={stats.submitted_for_sale} />
                      <StoreStat label={t('returns.statusAwaitingReceipt')} value={stats.in_transit} />
                      <StoreStat label={t('chatApp.storesOtherPvz')} value={stats.other_pvz_in_transit} />
                      <StoreStat label={t('chatApp.storesInProcessing')} value={stats.in_processing} />
                      <StoreStat label={t('chatApp.storesWhSale')} value={stats.wh_sale} />
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        </>
      )}

      <AddStoreModal
        show={showAddStore}
        onHide={() => setShowAddStore(false)}
        onStoreAdded={handleStoreAdded}
      />

      <Modal show={showEditStore} onHide={() => setShowEditStore(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('stores.editTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editStoreError && <Alert variant="danger">{editStoreError}</Alert>}
          <Form.Label>{t('stores.storeName')}</Form.Label>
          <Form.Control
            value={editStoreName}
            onChange={(e) => setEditStoreName(e.target.value)}
            placeholder={t('stores.storeNamePlaceholder')}
          />
          <Form.Text className="text-muted">{t('stores.storeNameHint')}</Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditStore(false)}>
            {t('common.cancel')}
          </Button>
          <Button className="btn-gradient" onClick={handleSaveStoreName} disabled={editStoreLoading}>
            {editStoreLoading ? <Spinner size="sm" /> : t('common.save')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteStore} onHide={() => setShowDeleteStore(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('stores.deleteStore')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('stores.deleteStoreConfirm')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteStore(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDeleteStore} disabled={deleteStoreLoading}>
            {deleteStoreLoading ? <Spinner size="sm" /> : t('stores.deleteStore')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showUpdateApiKey} onHide={() => setShowUpdateApiKey(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('stores.updateApiKey')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {updateApiKeyError && <Alert variant="danger">{updateApiKeyError}</Alert>}
          {updateApiKeySuccess && <Alert variant="success">{updateApiKeySuccess}</Alert>}
          <Form.Control
            type="password"
            value={newApiKey}
            onChange={(e) => setNewApiKey(e.target.value)}
            placeholder={t('stores.apiKeyPlaceholder')}
          />
          <Form.Text className="text-muted">{t('stores.apiKeyUpdateHint')}</Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateApiKey(false)}>
            {t('common.cancel')}
          </Button>
          <Button className="btn-gradient" onClick={handleSaveApiKey} disabled={updateApiKeyLoading}>
            {updateApiKeyLoading ? <Spinner size="sm" /> : t('common.save')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function StoreStat({ label, value }) {
  return (
    <div className="chat-app-store-stat">
      <span className="chat-app-store-stat-label">{label}</span>
      <span className="chat-app-store-stat-value">{value}</span>
    </div>
  );
}

function StoreActionsDropdown({ store, onEdit, onUpdateApiKey, onDelete, t }) {
  return (
    <Dropdown align="end" drop="down" className="chat-app-store-actions-dropdown">
      <Dropdown.Toggle variant="outline-secondary" size="sm" className="chat-app-store-actions-toggle">
        <i className="bi bi-three-dots-vertical" />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => onEdit(store)}>{t('stores.edit')}</Dropdown.Item>
        <Dropdown.Item onClick={() => onUpdateApiKey(store)}>{t('stores.updateApiKey')}</Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item className="text-danger" onClick={() => onDelete(store)}>
          {t('stores.deleteStore')}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
